<template>
  <CCard>
    <CCardHeader>
      <CIcon name="cil-chat-bubble" />
      <h5 class="d-inline ml-2">{{ isEditing ? `Edit Message #${editingMessageId}` : 'New Message' }}</h5>
      <div class="card-header-actions">
        <CButton color="dark" :to="prevRoute">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <form-summary
        v-if="!isValid"
        class="form-errors alert alert-danger mt-0"
        :validator="$v.form"
        :attributes="attributes"
      />

      <CForm class="message-form">
        <div class="d-flex flex-column">

          <div class="d-flex flex-column flex-md-row">
            <div class="form-group w-100 mr-2">
              <label class="d-block">Message Channels:</label>
              <v-select
                placeholder="Select.."
                v-model="$v.form.channels.$model"
                :options="$globalVar.messageChannels"
                :reduce="c => c.value"
                :searchable="false"
                :disabled="form.segment == 'Guest'"
                @input="getPushApps"
              />
            </div>

            <div class="form-group w-100">
              <label class="d-block">Segment:</label>
              <v-select
                placeholder="Select.."
                v-model="$v.form.segment.$model"
                :options="composerSegments"
                :reduce="c => c.value"
                :searchable="false"
              >
                <template #option="{ label, description }">
                  {{ label }}
                  <cite class="d-block ml-2 font-weight-normal text-muted"
                    style="font-size: 0.7em; font-size: calc(0.6em + 0.2vw);">{{ description }}</cite>
                </template>
              </v-select>
            </div>
          </div>

          <div class="d-flex flex-column flex-md-row" >
            <div class="form-group w-100" v-if="channelHasPush" :class="{ 'mr-2': showLeadRestaurantField }">
              <label class="d-block">Push Apps:</label>
              <v-select
                placeholder="Select.."
                v-model="$v.form.apps.$model"
                label="label"
                :options="pushApps"
                :searchable="false"
                :disabled="!channelHasPush || form.segment == 'Guest'"
                multiple
              >
                <template v-slot:option="app">
                  <strong>{{ /[A-Z]/.test(app.provider) ? app.provider : app.provider.toUpperCase() }}</strong> -
                  <span>({{ app.provider == "OneSignal" ? app.domain : app.app_id }})</span>
                </template>
              </v-select>
            </div>

            <div class="form-group w-100" v-if="showLeadRestaurantField">
              <label class="d-block">Reference Restaurant for Leads:</label>
              <v-select
                placeholder="Select.."
                v-model="$v.form.data['restaurant_id'].$model"
                label="label"
                :options="restaurants"
                :reduce="r => r.value"
                :searchable="false"
                :disabled="!showLeadRestaurantField"
                :loading="loading && restaurants.length == 0"
              />
            </div>
          </div>



          <!-- Settings -->
          <section class="settings">
            <div class="form-group w-100 mb-0">
              <h5>Settings</h5>

              <div v-if="showUserAudienceFilters" class="form-group w-100">
                <label class="d-block">User Filters:</label>
                <div class="d-flex flex-column flex-md-row flex-gap-3">
                  <CInputCheckbox
                    custom
                    label="Leads"
                    :checked.sync="$v.form.data['include_leads'].$model"
                  />
                  <CInputCheckbox
                    custom
                    label="Users with No Recent Orders"
                    :checked.sync="$v.form.data['filter_no_recent_orders'].$model"
                  />
                  <CInputCheckbox
                    custom
                    label="High Point Users"
                    :checked.sync="$v.form.data['filter_high_point_users'].$model"
                  />
                  <CInputCheckbox
                    custom
                    label="Not App Users"
                    :checked.sync="$v.form.data['filter_not_app_users'].$model"
                  />
                  <CInputCheckbox
                    custom
                    label="Not Main Site Users"
                    :checked.sync="$v.form.data['filter_not_main_site_users'].$model"
                  />
                  <CInputCheckbox
                    custom
                    label="Subsite Users"
                    :checked.sync="$v.form.data['filter_subsite_users'].$model"
                  />
                </div>
              </div>

              <div v-if="showUserAudienceFilters" class="form-group w-100">
                <label class="d-block">Geo Provinces:</label>
                <v-select
                  placeholder="All provinces"
                  v-model="$v.form.data['filter_geo_province_codes'].$model"
                  :options="geoProvinceOptions"
                  :reduce="province => province.value"
                  :loading="geoProvinceLoading"
                  multiple
                />
              </div>

              <div v-if="showUserAudienceFilters" class="form-group w-100">
                <label class="d-block">Audience Wave:</label>
                <div class="d-flex flex-column flex-md-row flex-gap-3">
                  <div class="w-100">
                    <label class="d-block">Total waves:</label>
                    <CInput
                      type="number"
                      min="2"
                      step="1"
                      :value.sync="$v.form.data['filter_wave_total'].$model"
                      placeholder="e.g. 10"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Wave number:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['filter_wave_number'].$model"
                      placeholder="e.g. 1"
                    />
                  </div>
                </div>
              </div>

              <div v-if="showNoRecentOrdersField" class="form-group w-100">
                <label class="d-block">No recent order days:</label>
                <CInput
                  type="number"
                  min="1"
                  step="1"
                  :value.sync="$v.form.data['no_recent_days'].$model"
                  placeholder="e.g. 90"
                />
              </div>

              <div v-if="showRetentionSettings" class="form-group w-100">
                <label class="d-block">Retention Settings:</label>
                <div class="d-flex flex-column flex-md-row flex-gap-3">
                  <div class="w-100">
                    <label class="d-block">Step:</label>
                    <CInput
                      type="number"
                      min="1"
                      max="4"
                      step="1"
                      :value.sync="$v.form.data['retention_step'].$model"
                      placeholder="1-4"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">No recent order days:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['no_recent_days'].$model"
                      placeholder="e.g. 30"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Retention key:</label>
                    <CInput
                      :value.sync="$v.form.data['retention_key'].$model"
                      placeholder="order_users_v1"
                    />
                  </div>
                </div>
                <div class="d-flex flex-column flex-md-row flex-gap-3 mt-2">
                  <div class="w-100">
                    <label class="d-block">Order channels:</label>
                    <v-select
                      placeholder="All order channels"
                      v-model="$v.form.data['retention_order_mediums'].$model"
                      :options="retentionOrderMediumOptions"
                      :reduce="medium => medium.value"
                      :searchable="false"
                      multiple
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Step gap days:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['retention_step_gap_days'].$model"
                      placeholder="e.g. 14"
                    />
                  </div>
                </div>
                <div class="d-flex flex-column flex-md-row flex-gap-3 mt-2">
                  <div class="w-100">
                    <label class="d-block">Push suppression days:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['push_suppression_days'].$model"
                      placeholder="e.g. 14"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Email suppression days:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['email_suppression_days'].$model"
                      placeholder="e.g. 30"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Fallback after hours:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['fallback_after_hours'].$model"
                      placeholder="e.g. 24"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Max users per run:</label>
                    <CInput
                      type="number"
                      min="1"
                      max="1000000"
                      step="1"
                      :value.sync="$v.form.data['max_users'].$model"
                      placeholder="e.g. 50000"
                    />
                  </div>
                </div>
              </div>

              <div v-if="showUniqueVoucherSettings" class="form-group w-100">
                <label class="d-block">Unique voucher:</label>
                <div class="d-flex align-items-center flex-gap-3 mb-2">
                  <CSwitch
                    :checked.sync="$v.form.data['retention_voucher_enabled'].$model"
                    labelOn="YES"
                    labelOff="NO"
                    color="success"
                    shape="pill"
                  />
                </div>
                <div v-if="form.data['retention_voucher_enabled']" class="d-flex flex-column flex-md-row flex-gap-3">
                  <div class="w-100">
                    <label class="d-block">Discount amount:</label>
                    <CInput
                      type="number"
                      min="0"
                      step="0.01"
                      :value.sync="$v.form.data['retention_voucher_discount_amount'].$model"
                      placeholder="7"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Valid days:</label>
                    <CInput
                      type="number"
                      min="1"
                      step="1"
                      :value.sync="$v.form.data['retention_voucher_valid_days'].$model"
                      placeholder="14"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Minimum order amount:</label>
                    <CInput
                      type="number"
                      min="0"
                      step="1"
                      :value.sync="$v.form.data['retention_voucher_min_order_amount'].$model"
                      placeholder="10"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Scope:</label>
                    <CSelect
                      :options="retentionVoucherScopeOptions"
                      :value.sync="$v.form.data['retention_voucher_scope'].$model"
                    />
                  </div>
                  <div class="w-100">
                    <label class="d-block">Delivery only:</label>
                    <CSwitch
                      :checked.sync="$v.form.data['retention_voucher_only_delivery'].$model"
                      labelOn="YES"
                      labelOff="NO"
                      color="success"
                      shape="pill"
                    />
                  </div>
                </div>
              </div>

              <div v-if="form.segment === 'MenutePayLowUsage'" class="form-group w-100">
                <label class="d-block">Usage threshold (%):</label>
                <CInput
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  :value.sync="$v.form.data['menutepay_usage_below_percent'].$model"
                  placeholder="e.g. 80"
                />
              </div>

              <!-- Use Voucher (optional) -->
              <div v-if="isApplicableCampaign" class="form-group w-100">
                <label class="d-block">Use Voucher (optional):</label>
                <v-select
                  :placeholder="vouchers.length == 0 ? 'Search..' : 'Select or search..'"
                  v-model="$v.form.voucher.$model"
                  :options="vouchers"
                  label="code"
                  :searchable="true"
                  :loading="loading && vouchers.length == 0"
                  @search="searchVouchers"
                >
                  <template v-slot:option="v">
                    {{ v.code }}
                    <br />
                    <cite class="small">{{ v.name }}</cite>
                  </template>
                  <span slot="no-options">Type to search vouchers..</span>
                </v-select>
              </div>

              <!-- Custom Link (optional) -->
              <div v-if="channelHasPush" class="form-group w-100">
                <label class="d-block">Custom Link (optional):</label>
                <div class="d-flex flex-column flex-md-row">
                  <CInput
                    class="w-100 mb-1 mb-md-0 mr-2"
                    placeholder="URL"
                    :value.sync="$v.form.data['custom_link']['url'].$model"
                  />
                  <CInput
                    class="w-100 mb-0"
                    placeholder="Title"
                    :value.sync="$v.form.data['custom_link']['title'].$model"
                  />
                </div>
              </div>

              <!-- Message Image (optional) -->
              <div v-if="channelHasPush" class="form-group w-100">
                <label class="d-block">Message Image (optional):</label>
                <div class="d-flex flex-column flex-md-row align-items-end">
                  <div class="w-100 mb-1 mb-md-0 mr-2">
                    <CButton 
                      color="dark" 
                      variant="outline" 
                      @click="$store.commit('set', ['selectNewImageAsideShow', true])"
                    >
                      <CIcon name="cil-image" class="align-bottom" /> Select Image
                    </CButton>
                    <div v-if="form.data.image" class="mt-2">
                      <img 
                        :src="form.data.image.image_url" 
                        alt="Selected image" 
                        style="max-width: 200px; max-height: 100px; border-radius: 5px;"
                      />
                      <CButton 
                        color="danger" 
                        variant="ghost" 
                        size="sm" 
                        class="ml-2"
                        @click="removeImage"
                      >
                        <CIcon name="cil-x" />
                      </CButton>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex flex-column flex-md-row">
                <!-- Put message in inbox -->
                <div class="form-group w-100 mr-2 mb-0">
                  <label class="d-block">Put Inbox:</label>
                  <div class="d-flex flex-gap-3">
                    <CSwitch
                      :checked.sync="$v.form.put_inbox.$model"
                      labelOn="YES"
                      labelOff="NO"
                      color="success"
                      shape="pill"
                    />
                    <div v-if="form.put_inbox" class="d-flex flex-column flex-md-row align-items-md-baseline flex-gap-2">
                      <div class="d-flex align-items-baseline flex-gap-1">
                        <label>Active Until:</label>
                        <CInput
                          type="date"
                          :value.sync="$v.form.active_until.$model"
                          :min="new Date().toISOString().slice(0,10)"
                          :disabled="form.active_until_is_null"
                        />
                      </div>
                      <CInputCheckbox
                        custom
                        label="No end date"
                        :checked.sync="$v.form.active_until_is_null.$model"
                      />
                    </div>
                  </div>
                </div>

                <!-- In-App Message -->
                <div v-if="channelHasPush" class="form-group w-100 mb-0">
                  <label class="d-block">In-App Message:</label>
                  <div class="d-flex flex-gap-3">
                    <CSwitch
                      :checked.sync="$v.form.data['in_app'].$model"
                      labelOn="YES"
                      labelOff="NO"
                      color="success"
                      shape="pill"
                    />

                    <div v-if="form.data['in_app'] === true" class="d-flex align-items-end flex-gap-1">
                      <CInputCheckbox
                        custom
                        label="Do not send as Push Notification"
                        :checked.sync="$v.form.data['disable_push_notification'].$model"
                      />
                    </div>
                </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Message -->
          <div class="d-flex flex-column flex-md-row message">
            <CSpinner color="primary" size="sm" v-if="!languages.length" />

            <CTabs v-else>
              <CTab v-for="(lang, index) in languages" :key="index" :title="lang.label" class="tab-item">
                <CInput
                  class="w-100 mb-2"
                  :placeholder="`${lang.label} title`"
                  :value.sync="$v.form.title[lang.value].$model"
                  :isValid="lang.value === 'nl' ? checkIfValid('title.nl') : null"
                />

                <div class="form-group message-editor">
                  <editor
                    v-model="$v.form.message[lang.value].$model"
                    api-key="hr96yvu80sl0l683adp0offp2wyhhcsu2n23iwl38tvwed63"
                    :init="{
                      plugins: 'wordcount,visualchars,table',
                      toolbar_mode: 'sliding',
                      toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | align lineheight | tinycomments | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                      tinycomments_mode: 'embedded',
                    }"
                  />
                </div>

                <div class="d-flex align-items-center justify-content-between">
                  <!-- <span>Characters: {{ form.message[lang.value].length }}</span> -->

                  <CDropdown color="link" :caret="false">
                    <template #toggler-content>
                      <div class="d-flex flex-gap-1 align-items-center">
                        <CIcon name="cil-plus" /> Add variable
                      </div>
                    </template>

                    <CDropdownItem v-for="(v, index) in variables" :key="index" @click="addVariable(lang.value, v.field)">
                      {{ v.label }}
                    </CDropdownItem>
                  </CDropdown>
                </div>
              </CTab>
            </CTabs>
          </div>
        </div>
      </CForm>
    </CCardBody>

    <CCardFooter class="sticky-bottom">
      <div class="d-flex align-items-center flex-gap-2">
        <CButton
          v-if="isEditing"
          color="primary"
          size="lg"
          :disabled="sending"
          @click="saveEdit"
        >
          <CSpinner v-if="sending" size="sm" class="mr-1" />
          <CIcon v-else name="cil-save" class="mr-1" />
          Save changes
        </CButton>

        <CDropdown v-else caret split color="primary" size="lg">
          <template #toggler-content>
            <div @click="send()" :class="{ 'disable-item': sending }">
              🚀 Send
            </div>
          </template>

          <CDropdownItem @click="showScheduleSendModal = true">
            <div class="d-flex align-items-center flex-gap-1">
              🗓️ Schedule send
            </div>
          </CDropdownItem>
          <CDropdownItem @click="send({ isTest: true })">
            <div class="d-flex align-items-center flex-gap-1">
              🧪 Send Test
            </div>
          </CDropdownItem>
          <CDropdownItem @click="send({ saveOnly: true })">
            <div class="d-flex align-items-center flex-gap-1">
              Save template
            </div>
          </CDropdownItem>
        </CDropdown>

        <div class="ml-auto d-flex align-items-center flex-wrap flex-gap-2 justify-content-end">
          <span v-if="audienceCountText" class="font-weight-bold text-info">
            {{ audienceCountText }}
          </span>

          <CButton
            color="info"
            variant="outline"
            :disabled="audienceCountLoading"
            @click="countAudience"
          >
            <CSpinner v-if="audienceCountLoading" size="sm" class="mr-1" />
            {{ audienceCountLoading ? 'Counting...' : 'Count recipients' }}
          </CButton>

          <CButton
            variant="ghost"
            color="danger"
            @click="resetForm"
          >
            <u>Reset Form</u>
          </CButton>
        </div>
      </div>
    </CCardFooter>

    <CModal class="schedule-send-modal" title="Pick date & time" color="white" :show.sync="showScheduleSendModal">
      <div class="d-flex">
        <v-date-picker
          mode="dateTime" is24hr :minute-increment="5"
          v-model="scheduledTime"
          :disabled-dates="{
            end: new Date().setDate(new Date().getDate() - 1),
          }"
        />

        <div v-if="scheduledTime" class="lead p-3 w-100">
          <span class="d-block border-bottom pb-2 mb-4">{{ momentScheduledTime.format("MMM Do YY") }}</span>
          <span class="d-block border-bottom pb-2 mb-4">{{ momentScheduledTime.format('LT') }}</span>
          <span v-if="momentScheduledTime.isSameOrBefore(new Date())" class="d-block text-danger">Invalid date & time</span>
          <span v-else class="d-block">* {{ momentScheduledTime.fromNow() }}</span>
        </div>
      </div>
      <template #footer>
        <CButton @click="showScheduleSendModal = false" color="link">Cancel</CButton>
        <CButton @click="scheduleSend"
          :disabled="scheduledTime === null || momentScheduledTime.isSameOrBefore(new Date())"
          color="primary"
          shape="pill">
          Schedule send
        </CButton>
      </template>
    </CModal>

    <!-- Select Image Sidebar -->
    <SelectImage @image-selected="onImageSelected" type="message" />
  </CCard>
</template>

<script>
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate"
import { required, requiredIf, minValue, maxValue } from "vuelidate/lib/validators"
import _debounce from 'lodash/debounce';
import Editor from '@tinymce/tinymce-vue';
import { DatePicker } from 'v-calendar';
import SelectImage from '@/components/Images/SelectImage.vue';

const voucherMustBeCompatibleWithRestaurant = (voucher, form) => {
  const usesLeadRestaurant = form.segment === 'Leads'
    || (form.segment === 'User' && !!form.data?.include_leads);

  return voucher && usesLeadRestaurant && form.data['restaurant_id']
    ? voucher.resto_id === null || voucher.resto_id === form.data['restaurant_id']
    : true;
};

export default {
  name: "NewMessage",
  components: {
    'editor': Editor,
    'v-date-picker': DatePicker,
    SelectImage,
  },
  data() {
    return {
      form: {},

      pushApps: [],
      languages: [],
      vouchers: [],
      restaurants: [],
      geoProvinceOptions: [],
      geoProvinceLoading: false,
      retentionOrderMediumOptions: [
        { value: 0, label: "Mainsite (M)" },
        { value: 1, label: "Subsite (S)" },
        { value: 2, label: "App (A)" },
      ],
      retentionVoucherScopeOptions: [
        { value: "all", label: "All channels" },
        { value: "app", label: "App only" },
      ],

      vars: [
        { field: "first_name", label: "User First Name" },
        { field: "email", label: "Email" },
        { field: "user.point", label: "User Point", segments: ["User", "UsersWithOrders", "HighPointUsers"] },
        { field: "user.point_euro", label: "User Point Value (EUR)", segments: ["User", "UsersWithOrders", "HighPointUsers"] },
        { field: "nearby_restaurant_count", label: "Nearby Restaurant Count", segments: ["UsersWithOrders"] },
        { field: "menutepay_limit_now", label: "MenutePay: Current Budget Limit", segments: ["MenutePayLowUsage"] },

        { field: "restaurant_name", label: "Restaurant: Name", type: "restaurant", segments: ["Restaurant", "Leads"] },
        { field: "restaurant_city", label: "Restaurant: City", type: "restaurant", segments: ["Restaurant", "Leads"] },
        { field: "restaurant_logo", label: "Restaurant: Logo", type: "restaurant", segments: ["Restaurant", "Leads"] },
        { field: "restaurant_list", label: "Restaurant List", segments: ["User", "UsersWithOrders", "NoRecentOrdersUsers", "InactiveOrderUsers", "MenutePayLowUsage", "HighPointUsers", "FirstOrderOrNewAccount22Hours", "Guest", "Admin", "Courier"] },
        { field: "menute_cta_button", label: "Menute CTA Button", segments: ["User", "Leads", "UsersWithOrders", "NoRecentOrdersUsers", "InactiveOrderUsers", "MenutePayLowUsage", "HighPointUsers", "FirstOrderOrNewAccount22Hours", "Admin", "Courier"] },
      ],
      campaignVars: [
        { field: "voucher_code", label: "Voucher: Code" },
        { field: "discount", label: "Voucher: Discount" },
        { field: "valid_until", label: "Voucher: Valid Until" },
      ],
      retentionVoucherVars: [
        { field: "retention_voucher_code", label: "Retention Voucher: Code" },
        { field: "retention_voucher_discount", label: "Retention Voucher: Discount" },
        { field: "retention_voucher_valid_until", label: "Retention Voucher: Valid Until" },
        { field: "retention_voucher_min_order_amount", label: "Retention Voucher: Minimum Order" },
      ],

      attributes: {
        channels: 'Message channels',
        apps: 'Push apps',
        'title.nl': 'Title in Nederlands',
        'message.nl': 'Message in Nederlands',
        voucher: 'Voucher',
        data: {
          restaurant_id: "Reference restaurant for leads",
          no_recent_days: "No recent order days",
          filter_high_point_users: "High Point Users",
          filter_not_app_users: "Not App Users",
          filter_not_main_site_users: "Not Main Site Users",
          filter_subsite_users: "Subsite Users",
          filter_geo_province_codes: "Geo Provinces",
          filter_wave_total: "Total waves",
          filter_wave_number: "Wave number",
          menutepay_usage_below_percent: "Usage threshold (%)",
          retention_order_mediums: "Retention order channels",
          retention_key: "Retention key",
          retention_step: "Retention step",
          retention_step_gap_days: "Retention step gap days",
          push_suppression_days: "Push suppression days",
          email_suppression_days: "Email suppression days",
          fallback_after_hours: "Fallback after hours",
          max_users: "Max users per run",
          retention_voucher_enabled: "Unique retention voucher",
          retention_voucher_discount_amount: "Retention voucher discount",
          retention_voucher_valid_days: "Retention voucher valid days",
          retention_voucher_min_order_amount: "Retention voucher minimum order",
          retention_voucher_scope: "Retention voucher scope",
          retention_voucher_only_delivery: "Retention voucher delivery only"
        }
      },

      sending: false,
      editingMessageId: null,
      audienceCountLoading: false,
      audienceCountResult: null,
      loading: false,
      prevRoute: null,
      scheduledTime: null,
      showScheduleSendModal: false,
    }
  },

  // Get previous page
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  mixins: [validationMixin],
  validations: {
    form: {
      channels: { required },
      apps: {
        required: requiredIf(function () {
          return this.channelHasPush;
        }),
      },
      segment: {},
      title: {
        nl: { required },
        fr: {},
        en: {},
      },
      message: {
        nl: { required },
        fr: {},
        en: {},
      },
      voucher: {
        voucherMustBeCompatibleWithRestaurant
        // required: requiredIf(function () {
        //   return !!this.form.campaign;
        // }),
      },
      data: {
        include_leads: {},
        filter_no_recent_orders: {},
        filter_high_point_users: {},
        filter_not_app_users: {},
        filter_not_main_site_users: {},
        filter_subsite_users: {},
        filter_geo_province_codes: {},
        filter_wave_total: {
          minValue: minValue(2),
        },
        filter_wave_number: {
          minValue: minValue(1),
        },
        restaurant_id: {
          required: requiredIf(function () {
            return this.showLeadRestaurantField;
          }),
        },
        no_recent_days: {
          required: requiredIf(function () {
            return this.showNoRecentOrdersField || this.showRetentionSettings;
          }),
          minValue: minValue(1),
        },
        retention_order_mediums: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
        },
        retention_key: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
        },
        retention_step: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
          minValue: minValue(1),
          maxValue: maxValue(4),
        },
        retention_step_gap_days: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
          minValue: minValue(1),
        },
        push_suppression_days: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
          minValue: minValue(1),
        },
        email_suppression_days: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
          minValue: minValue(1),
        },
        fallback_after_hours: {
          required: requiredIf(function () {
            return this.showRetentionSettings;
          }),
          minValue: minValue(1),
        },
        max_users: {
          minValue: minValue(1),
          maxValue: maxValue(1000000),
        },
        retention_voucher_enabled: {},
        retention_voucher_discount_amount: {
          required: requiredIf(function () {
            return this.showUniqueVoucherSettings && this.form.data['retention_voucher_enabled'];
          }),
          minValue: minValue(0),
        },
        retention_voucher_valid_days: {
          required: requiredIf(function () {
            return this.showUniqueVoucherSettings && this.form.data['retention_voucher_enabled'];
          }),
          minValue: minValue(1),
        },
        retention_voucher_min_order_amount: {
          required: requiredIf(function () {
            return this.showUniqueVoucherSettings && this.form.data['retention_voucher_enabled'];
          }),
          minValue: minValue(0),
        },
        retention_voucher_scope: {
          required: requiredIf(function () {
            return this.showUniqueVoucherSettings && this.form.data['retention_voucher_enabled'];
          }),
        },
        retention_voucher_only_delivery: {},
        menutepay_usage_below_percent: {
          required: requiredIf(function () {
            return this.form.segment === 'MenutePayLowUsage';
          }),
          minValue: minValue(1),
          maxValue: maxValue(100),
        },
        custom_link : {
          url : {},
          title: {},
        },
        in_app: {},
        disable_push_notification: {},
      },
      put_inbox: {},
      active_until: {},
      active_until_is_null: {},
    }
  },

  computed: {
    ...mapGetters(["errors"]),
    isValid() { return !this.$v.form.$invalid },
    isDirty() { return this.$v.form.$anyDirty },
    isEditing() { return !!this.editingMessageId },
    // channelHasPush() { return this.form.channels.includes('push'); },
    channelHasPush() { return this.form.channels == 'push'; },
    composerSegments() {
      return this.$globalVar.messageSegments.filter(segment => !['Leads', 'NoRecentOrdersUsers', 'HighPointUsers'].includes(segment.value));
    },
    showUserAudienceFilters() { return this.form.segment === 'User'; },
    currentCountryFilter() { return this.$store.state.filters.country; },
    showLeadRestaurantField() {
      return this.form.segment === 'Leads'
        || (this.form.segment === 'User' && !!this.form.data?.include_leads);
    },
    showNoRecentOrdersField() {
      return this.form.segment === 'NoRecentOrdersUsers'
        || (this.form.segment === 'User' && !!this.form.data?.filter_no_recent_orders);
    },
    showRetentionSettings() { return this.form.segment === 'InactiveOrderUsers'; },
    showUniqueVoucherSettings() {
      return this.segmentSupportsUniqueVoucher(this.form.segment);
    },
    canUseRestaurantVars() {
      return this.form.segment === 'Restaurant' || this.showLeadRestaurantField;
    },
    isApplicableCampaign() { return ["User", "UsersWithOrders", "Leads", "Guest", "NoRecentOrdersUsers", "MenutePayLowUsage", "HighPointUsers", "FirstOrderOrNewAccount22Hours"].includes(this.form.segment) },
    selectedAudienceFilters() {
      return this.getAudienceFilters(this.form);
    },
    audienceSignature() {
      const data = this.form.data || {};
      const apps = Array.isArray(this.form.apps)
        ? this.form.apps.map(app => app?.id ?? app).join(',')
        : this.form.apps;

      return JSON.stringify({
        segment: this.form.segment,
        channels: this.form.channels,
        apps,
        voucher: this.form.voucher?.id ?? this.form.voucher,
        put_inbox: this.form.put_inbox,
        active_until_is_null: this.form.active_until_is_null,
        include_leads: !!data.include_leads,
        filter_no_recent_orders: !!data.filter_no_recent_orders,
        filter_high_point_users: !!data.filter_high_point_users,
        filter_not_app_users: !!data.filter_not_app_users,
        filter_not_main_site_users: !!data.filter_not_main_site_users,
        filter_subsite_users: !!data.filter_subsite_users,
        filter_geo_province_codes: Array.isArray(data.filter_geo_province_codes)
          ? [...data.filter_geo_province_codes].sort().join(',')
          : '',
        filter_wave_total: data.filter_wave_total || '',
        filter_wave_number: data.filter_wave_number || '',
        restaurant_id: data.restaurant_id,
        no_recent_days: data.no_recent_days,
        retention_order_mediums: Array.isArray(data.retention_order_mediums)
          ? [...data.retention_order_mediums].sort().join(',')
          : '',
        retention_key: data.retention_key,
        retention_step: data.retention_step,
        retention_step_gap_days: data.retention_step_gap_days,
        push_suppression_days: data.push_suppression_days,
        email_suppression_days: data.email_suppression_days,
        fallback_after_hours: data.fallback_after_hours,
        max_users: data.max_users,
        retention_voucher_enabled: data.retention_voucher_enabled,
        retention_voucher_discount_amount: data.retention_voucher_discount_amount,
        retention_voucher_valid_days: data.retention_voucher_valid_days,
        retention_voucher_min_order_amount: data.retention_voucher_min_order_amount,
        retention_voucher_scope: data.retention_voucher_scope,
        retention_voucher_only_delivery: data.retention_voucher_only_delivery,
        menutepay_usage_below_percent: data.menutepay_usage_below_percent,
      });
    },
    audienceCountText() {
      if (!this.audienceCountResult) {
        return '';
      }

      const count = Number(this.audienceCountResult.recipient_count || 0);
      const type = this.audienceCountResult.recipient_type || 'users';
      const unit = {
        users: count === 1 ? 'user' : 'users',
        devices: count === 1 ? 'device' : 'devices',
        restaurants: count === 1 ? 'restaurant' : 'restaurants',
      }[type] || type;

      const uncappedCount = Number(this.audienceCountResult.uncapped_recipient_count || 0);
      const isLimited = uncappedCount > count;
      const limitedText = isLimited
        ? ` (limited from ${uncappedCount.toLocaleString()})`
        : '';

      return `Selected recipients: ${count.toLocaleString()} ${unit}${limitedText}`;
    },
    variables() {
      const vars = this.vars.filter(v => {
        if (v.type === 'restaurant') {
          return this.canUseRestaurantVars;
        }

        return !v.segments || v.segments.includes(this.form.segment);
      });

      const availableVars = this.form.voucher
        ? [
          ...vars,
          ...this.campaignVars
        ]
        : vars;

      return this.showUniqueVoucherSettings && this.form.data?.retention_voucher_enabled
        ? [
          ...availableVars,
          ...this.retentionVoucherVars
        ]
        : availableVars;
    },
    momentScheduledTime() { return this.moment(this.scheduledTime) },
  },

  watch: {
    form: {
      handler: function (val) {
        const draft = { ...val };
        if (this.editingMessageId) {
          draft._edit_message_id = this.editingMessageId;
        }
        localStorage.setItem('user-message', JSON.stringify(draft));
      },
      deep: true,
    },

    audienceSignature: function () {
      this.audienceCountResult = null;
    },

    channelHasPush: function (newVal) {
      if (!newVal) {
        this.form.data.image = null;
      }
    },

    'form.voucher': async function (newVal, oldVal) {
      const confirmReset = async () => {
        const confirm = await this.mSwal.fire({
          title: "Use the default campaign template?",
          icon: "question",
        });
        return confirm.isConfirmed;
      };

      if (!oldVal && newVal) {
        if (await confirmReset()) {
          await this.getCampaignMailContents();
        }
      }
    },

    showLeadRestaurantField: async function (newVal) {
      if (newVal && this.restaurants.length === 0) {
        await this.getAllRestaurants();
      }
    },

    showUserAudienceFilters: async function (newVal) {
      if (newVal && this.geoProvinceOptions.length === 0) {
        await this.getGeoProvinceOptions();
      }
    },

    currentCountryFilter: async function () {
      if (!this.form?.data) {
        return;
      }

      this.form.data.filter_geo_province_codes = [];
      await this.getGeoProvinceOptions(true);
    },

    'form.segment': async function (newVal, oldVal) {
      if (!this.isApplicableCampaign) {
        this.form.voucher = null;
      }

      this.normalizeUniqueVoucherData(this.form.data, newVal);

      if (newVal === 'InactiveOrderUsers') {
        const preferFreshRetentionDefaults = oldVal !== undefined && oldVal !== 'InactiveOrderUsers';
        this.ensureRetentionDefaults(preferFreshRetentionDefaults);

        if (this.channelHasPush) {
          await this.getPushApps();
          this.form.apps = this.pushApps.filter(app => app.app_id == 'fcm-menute-enduser');
        }
      }

      if (newVal === 'Guest') {
        // this.form.channels = ["push"];
        this.form.channels = "push";
        await this.getPushApps();

        this.form.apps = this.pushApps.filter(app => app.app_id == 'fcm-menute-enduser');
      }
    },

    'form.put_inbox': function (newVal) {
      if (!newVal) {
        this.form.active_until = null;
        this.form.active_until_is_null = false;
      }
    },

    'form.active_until_is_null': function (newVal) {
      if (newVal) {
        this.form.active_until = null;
      }
    },

    'form.data.in_app': function (newVal) {
      if (!newVal) {
        this.form.data.disable_push_notification = false;
      }
    },
  },

  async beforeMount() {
    this.searchVouchers = _debounce(async (val) => {
      await this.getVouchers(val);
    }, 500);

    this.form = this.getForm();

    if (this.showLeadRestaurantField) {
      await this.getAllRestaurants();
    }

    if (this.showUserAudienceFilters) {
      await this.getGeoProvinceOptions();
    }

    if (this.channelHasPush) {
      await this.getPushApps();

      if (this.form.apps !== null && typeof this.form.apps === "string") {
        const apps = this.form.apps.split(',').map(Number);
        this.form.apps = this.pushApps.filter(function (app) {
          return apps.includes(app.id);
        });
      }
    }
  },

  async mounted() {
    this.loading = true;
    await this.getAllLanguages();
    this.loading = false;
  },

  methods: {
    checkIfValid(fieldName) {
      const fields = fieldName.split(".");
      const field = fields.length > 1
        ? this.$v.form[fields[0]][fields[1]]
        : this.$v.form[fieldName];

      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === "");
    },

    getForm() {
      const defaultForm = {
        // "channels": ["email"],
        "channels": "push",
        "apps": [],
        "segment": "User",
        "title": {
          "nl": "",
          "fr": "",
          "en": ""
        },
        "message": {
          "nl": "",
          "fr": "",
          "en": ""
        },
        "voucher": null,
        "put_inbox": true,
        "active_until": null,
        "active_until_is_null": false,
        "data": {
          "include_leads": false,
          "filter_no_recent_orders": false,
          "filter_high_point_users": false,
          "filter_not_app_users": false,
          "filter_not_main_site_users": false,
          "filter_subsite_users": false,
          "filter_geo_province_codes": [],
          "filter_wave_total": null,
          "filter_wave_number": null,
          "restaurant_id": null,
          "no_recent_days": 90,
          "retention_order_mediums": [0, 1, 2],
          "retention_key": "order_users_v1",
          "retention_step": 1,
          "retention_step_gap_days": 14,
          "push_suppression_days": 14,
          "email_suppression_days": 30,
          "fallback_after_hours": 24,
          "max_users": null,
          "retention_voucher_enabled": false,
          "retention_voucher_discount_amount": 7,
          "retention_voucher_valid_days": 14,
          "retention_voucher_min_order_amount": 10,
          "retention_voucher_scope": "all",
          "retention_voucher_only_delivery": false,
          "menutepay_usage_below_percent": 80,
          "custom_link": {
            "url": null,
            "title": null
          },
          "in_app": false,
          "disable_push_notification": false,
          "image": null,
        }
      };

      const saved = JSON.parse(localStorage.getItem('user-message'));
      if (!saved) return defaultForm;

      this.editingMessageId = saved._edit_message_id || null;
      delete saved._edit_message_id;

      return this.normalizeFormDraft(saved, defaultForm);
    },

    normalizeFormDraft(saved, defaultForm) {
      const normalized = {
        ...defaultForm,
        ...saved,
        data: {
          ...defaultForm.data,
          ...(saved.data || {}),
        },
      };

      if (normalized.segment === 'Leads') {
        normalized.segment = 'User';
        normalized.data.include_leads = true;
      }

      if (normalized.segment === 'NoRecentOrdersUsers') {
        normalized.segment = 'User';
        normalized.data.filter_no_recent_orders = true;
      }

      if (normalized.segment === 'HighPointUsers') {
        normalized.segment = 'User';
        normalized.data.filter_high_point_users = true;
      }

      normalized.data.include_leads = !!normalized.data.include_leads;
      normalized.data.filter_no_recent_orders = !!normalized.data.filter_no_recent_orders;
      normalized.data.filter_high_point_users = !!normalized.data.filter_high_point_users;
      normalized.data.filter_not_app_users = !!normalized.data.filter_not_app_users;
      normalized.data.filter_not_main_site_users = !!normalized.data.filter_not_main_site_users;
      normalized.data.filter_subsite_users = !!normalized.data.filter_subsite_users;
      normalized.data.filter_geo_province_codes = Array.isArray(normalized.data.filter_geo_province_codes)
        ? normalized.data.filter_geo_province_codes
        : [];
      normalized.data.filter_wave_total = normalized.data.filter_wave_total
        ? Number(normalized.data.filter_wave_total)
        : null;
      normalized.data.filter_wave_number = normalized.data.filter_wave_number
        ? Number(normalized.data.filter_wave_number)
        : null;
      normalized.active_until_is_null = !!normalized.active_until_is_null;
      if (!normalized.put_inbox) {
        normalized.active_until_is_null = false;
      }
      if (normalized.active_until_is_null) {
        normalized.active_until = null;
      }
      this.normalizeRetentionData(normalized.data);
      this.normalizeUniqueVoucherData(normalized.data, normalized.segment);

      return normalized;
    },

    normalizeRetentionData(data, preferFresh = false) {
      if (!data) {
        return data;
      }

      if (preferFresh) {
        data.no_recent_days = 30;
        data.retention_step = 1;
      }

      data.retention_order_mediums = Array.isArray(data.retention_order_mediums) && data.retention_order_mediums.length
        ? data.retention_order_mediums.map(Number).filter(value => [0, 1, 2].includes(value))
        : [0, 1, 2];

      if (data.retention_order_mediums.length === 0) {
        data.retention_order_mediums = [0, 1, 2];
      }

      data.retention_key = data.retention_key || 'order_users_v1';
      data.no_recent_days = data.no_recent_days ? Number(data.no_recent_days) : 30;
      data.retention_step = data.retention_step ? Number(data.retention_step) : 1;
      data.retention_step_gap_days = data.retention_step_gap_days ? Number(data.retention_step_gap_days) : 14;
      data.push_suppression_days = data.push_suppression_days ? Number(data.push_suppression_days) : 14;
      data.email_suppression_days = data.email_suppression_days ? Number(data.email_suppression_days) : 30;
      data.fallback_after_hours = data.fallback_after_hours ? Number(data.fallback_after_hours) : 24;
      data.max_users = data.max_users ? Number(data.max_users) : null;

      return data;
    },

    ensureRetentionDefaults(preferFresh = false) {
      this.normalizeRetentionData(this.form.data, preferFresh);
      this.normalizeUniqueVoucherData(this.form.data, this.form.segment, preferFresh);
    },

    normalizeUniqueVoucherData(data, segment = this.form?.segment, preferFresh = false) {
      if (!data) {
        return data;
      }

      if (!this.segmentSupportsUniqueVoucher(segment)) {
        data.retention_voucher_enabled = null;
        data.retention_voucher_discount_amount = null;
        data.retention_voucher_valid_days = null;
        data.retention_voucher_min_order_amount = null;
        data.retention_voucher_scope = null;
        data.retention_voucher_only_delivery = null;
        return data;
      }

      const hasExplicitEnabled = data.retention_voucher_enabled === true
        || data.retention_voucher_enabled === false;
      data.retention_voucher_enabled = preferFresh || !hasExplicitEnabled
        ? segment === 'InactiveOrderUsers'
        : data.retention_voucher_enabled;
      data.retention_voucher_discount_amount = data.retention_voucher_discount_amount ? Number(data.retention_voucher_discount_amount) : 7;
      data.retention_voucher_valid_days = data.retention_voucher_valid_days ? Number(data.retention_voucher_valid_days) : 14;
      data.retention_voucher_min_order_amount = data.retention_voucher_min_order_amount !== null && data.retention_voucher_min_order_amount !== ''
        ? Number(data.retention_voucher_min_order_amount)
        : 10;
      data.retention_voucher_scope = ['all', 'app'].includes(data.retention_voucher_scope)
        ? data.retention_voucher_scope
        : 'all';
      data.retention_voucher_only_delivery = !!data.retention_voucher_only_delivery;

      return data;
    },

    segmentSupportsUniqueVoucher(segment) {
      return ["User", "UsersWithOrders", "Leads", "NoRecentOrdersUsers", "InactiveOrderUsers", "MenutePayLowUsage", "HighPointUsers", "FirstOrderOrNewAccount22Hours"].includes(segment);
    },

    async resetForm() {
      const confirmReset = async () => {
        const confirm = await this.mSwal.fire({
          title: "The form will be reset. Are you sure you want to continue?",
          icon: "warning",
        });
        return confirm.isConfirmed;
      };

      if (await confirmReset()) {
        localStorage.removeItem('user-message')
        this.editingMessageId = null;
        this.form = this.getForm();
      }
    },

    addVariable(lang, field) {
      this.form.message[lang] += `{{${field}}}`
    },

    isFormIncludeVar(variable) {
      var { title, message } = this.form;
      return Object.values({ title, message }).some(langs =>
        Object.values(langs).some(text =>
          text.includes("{{" + variable + "}}")
        )
      );
    },

    isFormIncludeCampaignVars() {
      var { title, message } = this.form;

      return Object.values({ title, message }).some(langs =>
        Object.values(langs).some(text =>
          this.campaignVars.some(v => text.includes("{{" + v.field + "}}"))
        )
      );
    },

    isFormIncludeRetentionVoucherVars() {
      var { title, message } = this.form;

      return Object.values({ title, message }).some(langs =>
        Object.values(langs).some(text =>
          this.retentionVoucherVars.some(v => text.includes("{{" + v.field + "}}"))
        )
      );
    },

    isFormIncludeRestaurantVars() {
      var { title, message } = this.form;
      const restaurantVars = this.vars.filter(v => v.type == "restaurant");

      return Object.values({ title, message }).some(langs =>
        Object.values(langs).some(text =>
          restaurantVars.some(v => text.includes("{{" + v.field + "}}"))
        )
      );
    },

    getAudienceFilters(source) {
      const segment = source?.segment;
      const data = source?.data || {};

      if (segment === 'Leads') {
        return ['Leads', this.formatRetentionVoucherFilter(data)].filter(Boolean);
      }

      if (segment === 'NoRecentOrdersUsers') {
        return ['Users with No Recent Orders', this.formatRetentionVoucherFilter(data)].filter(Boolean);
      }

      if (segment === 'HighPointUsers') {
        return ['High Point Users', this.formatRetentionVoucherFilter(data)].filter(Boolean);
      }

      if (segment === 'InactiveOrderUsers') {
        return this.getRetentionAudienceFilters(data);
      }

      if (segment !== 'User') {
        return [this.formatRetentionVoucherFilter(data)].filter(Boolean);
      }

      return [
        data.include_leads ? 'Leads' : null,
        data.filter_no_recent_orders ? 'Users with No Recent Orders' : null,
        data.filter_high_point_users ? 'High Point Users' : null,
        data.filter_not_app_users ? 'Not App Users' : null,
        data.filter_not_main_site_users ? 'Not Main Site Users' : null,
        data.filter_subsite_users ? 'Subsite Users' : null,
        this.formatAudienceWaveFilter(data.filter_wave_total, data.filter_wave_number),
        this.formatGeoProvinceFilter(data.filter_geo_province_codes),
        this.formatRetentionVoucherFilter(data),
      ].filter(Boolean);
    },

    getSegmentLabel(source) {
      const segment = typeof source === 'string'
        ? source
        : source?.segment;

      return this.$globalVar.messageSegments.find(option => option.value === segment)?.label || segment;
    },

    formatGeoProvinceFilter(codes) {
      if (!Array.isArray(codes) || codes.length === 0) {
        return null;
      }

      const labels = codes.map(code => {
        const option = this.geoProvinceOptions.find(province => province.value === code);
        return option?.label || code;
      });

      return `Geo Provinces: ${labels.join(', ')}`;
    },

    getRetentionAudienceFilters(data = {}) {
      return [
        `Step ${data.retention_step || 1}/4`,
        `No order since ${data.no_recent_days || 30} days`,
        this.formatRetentionMediumFilter(data.retention_order_mediums),
        data.retention_key ? `Key: ${data.retention_key}` : null,
        data.push_suppression_days ? `Push cooldown: ${data.push_suppression_days}d` : null,
        data.email_suppression_days ? `Email cooldown: ${data.email_suppression_days}d` : null,
        data.fallback_after_hours ? `Fallback: ${data.fallback_after_hours}h` : null,
        this.formatMaxUsersFilter(data.max_users),
        this.formatRetentionVoucherFilter(data),
      ].filter(Boolean);
    },

    formatMaxUsersFilter(maxUsers) {
      return maxUsers
        ? `Max users: ${Number(maxUsers).toLocaleString()}`
        : null;
    },

    formatRetentionVoucherFilter(data = {}) {
      if (data.retention_voucher_enabled !== true) {
        return null;
      }

      const amount = data.retention_voucher_discount_amount || 7;
      const days = data.retention_voucher_valid_days || 14;
      const minOrder = data.retention_voucher_min_order_amount || 10;
      const scope = data.retention_voucher_scope === 'app' ? 'app only' : 'all channels';
      const delivery = data.retention_voucher_only_delivery ? ', delivery only' : '';

      return `Voucher: EUR ${amount}, ${days}d, min EUR ${minOrder}, ${scope}${delivery}`;
    },

    formatRetentionMediumFilter(values) {
      const selected = Array.isArray(values) && values.length ? values.map(Number) : [0, 1, 2];
      const labels = this.retentionOrderMediumOptions
        .filter(option => selected.includes(option.value))
        .map(option => option.label);

      return labels.length ? `Channels: ${labels.join(', ')}` : null;
    },

    formatAudienceWaveFilter(total, number) {
      if (!total || !number) {
        return null;
      }

      return `Wave ${number}/${total}`;
    },

    geoProvinceOptionsCacheKey() {
      return `crm-message-geo-provinces-${this.currentCountryFilter || 'all'}`;
    },

    async getGeoProvinceOptions(forceRefresh = false) {
      const cacheKey = this.geoProvinceOptionsCacheKey();
      const cachedValue = !forceRefresh ? sessionStorage.getItem(cacheKey) : null;

      if (cachedValue) {
        try {
          this.geoProvinceOptions = JSON.parse(cachedValue);
          return;
        } catch (error) {
          sessionStorage.removeItem(cacheKey);
        }
      }

      this.geoProvinceLoading = true;

      try {
        const { data } = await this.$axios.get(this.$backend.CRM_MESSAGES.GEO_PROVINCES, {
          params: {
            country: this.currentCountryFilter,
          },
        });

        this.geoProvinceOptions = data;
        sessionStorage.setItem(cacheKey, JSON.stringify(this.geoProvinceOptions));
      } catch (error) {
        this.geoProvinceOptions = [];
      } finally {
        this.geoProvinceLoading = false;
      }
    },

    buildPayload({ executionTime = null, isTest = false, saveOnly = false } = {}) {
      const payload = {
        ...this.form,
        data: {
          ...this.form.data,
          custom_link: {
            ...this.form.data.custom_link,
          },
          is_test: isTest,
        },
        channels: this.form.channels,
        apps: this.form.apps?.pluck('id').join(','),
        voucher: this.form.voucher?.id,
        save_only: saveOnly,
        scheduled_time: saveOnly ? null : (executionTime ? this.momentScheduledTime.format('YYYY-MM-DD HH:mm') : null)
      };

      if (payload.segment === 'User') {
        if (!payload.data.include_leads) {
          payload.data.restaurant_id = null;
        }

        if (!payload.data.filter_no_recent_orders) {
          payload.data.no_recent_days = null;
        }

        if (!payload.data.filter_high_point_users) {
          payload.data.filter_high_point_users = null;
        }

        if (!payload.data.filter_not_app_users) {
          payload.data.filter_not_app_users = null;
        }

        if (!payload.data.filter_not_main_site_users) {
          payload.data.filter_not_main_site_users = null;
        }

        if (!payload.data.filter_subsite_users) {
          payload.data.filter_subsite_users = null;
        }

        if (!Array.isArray(payload.data.filter_geo_province_codes) || payload.data.filter_geo_province_codes.length === 0) {
          payload.data.filter_geo_province_codes = null;
        }

        const hasWaveTotal = payload.data.filter_wave_total !== null && payload.data.filter_wave_total !== '';
        const hasWaveNumber = payload.data.filter_wave_number !== null && payload.data.filter_wave_number !== '';
        if (!hasWaveTotal && !hasWaveNumber) {
          payload.data.filter_wave_total = null;
          payload.data.filter_wave_number = null;
        } else {
          payload.data.filter_wave_total = hasWaveTotal ? Number(payload.data.filter_wave_total) : null;
          payload.data.filter_wave_number = hasWaveNumber ? Number(payload.data.filter_wave_number) : null;
        }
      } else {
        payload.data.include_leads = null;
        payload.data.filter_no_recent_orders = null;
        payload.data.filter_high_point_users = null;
        payload.data.filter_not_app_users = null;
        payload.data.filter_not_main_site_users = null;
        payload.data.filter_subsite_users = null;
        payload.data.filter_geo_province_codes = null;
        payload.data.filter_wave_total = null;
        payload.data.filter_wave_number = null;

        if (payload.segment !== 'Leads') {
          payload.data.restaurant_id = null;
        }

        if (!['NoRecentOrdersUsers', 'InactiveOrderUsers'].includes(payload.segment)) {
          payload.data.no_recent_days = null;
        }
      }

      if (payload.segment === 'InactiveOrderUsers') {
        this.normalizeRetentionData(payload.data);
      } else {
        payload.data.retention_order_mediums = null;
        payload.data.retention_key = null;
        payload.data.retention_step = null;
        payload.data.retention_step_gap_days = null;
        payload.data.push_suppression_days = null;
        payload.data.email_suppression_days = null;
        payload.data.fallback_after_hours = null;
        payload.data.max_users = null;
      }

      if (this.segmentSupportsUniqueVoucher(payload.segment)) {
        this.normalizeUniqueVoucherData(payload.data, payload.segment);
      } else {
        payload.data.retention_voucher_enabled = null;
        payload.data.retention_voucher_discount_amount = null;
        payload.data.retention_voucher_valid_days = null;
        payload.data.retention_voucher_min_order_amount = null;
        payload.data.retention_voucher_scope = null;
        payload.data.retention_voucher_only_delivery = null;
      }

      if (payload?.data?.image) {
        const { id, image_url } = payload.data.image;
        payload.data.image = { id, image_url };
      }

      payload.active_until_is_null = !!(payload.put_inbox && payload.active_until_is_null);
      if (!payload.put_inbox || payload.active_until_is_null) {
        payload.active_until = null;
      }

      return payload;
    },

    async saveEdit() {
      if (!this.editingMessageId) {
        this.$toast.error('No message selected for editing.');
        return;
      }

      await this.send({ saveOnly: true, updateExisting: true });
    },

    async send({ executionTime = null, isTest = false, saveOnly = false, updateExisting = false } = {}) {
      this.form.data.in_app ||= !!(this.form.data.custom_link.url);

      if (!this.isValid) {
        this.$v.$touch();
        return;
      }

      const userPointVariables = ["user.point", "user.point_euro"];
      const canUseUserPointVariables = ["User", "UsersWithOrders", "HighPointUsers"].includes(this.form.segment);

      if (!canUseUserPointVariables && userPointVariables.some(variable => this.isFormIncludeVar(variable))) {
        this.$toast.error(`User point variables can only be used in the "User", "Users with Orders" and "High Point Users" segments.`);
        return;
      }

      if (!["UsersWithOrders"].includes(this.form.segment) && this.isFormIncludeVar("nearby_restaurant_count")) {
        this.$toast.error(`Nearby restaurant count variable can only be used in the "Users with Orders" segment.`);
        return;
      }

      if (!this.canUseRestaurantVars && this.isFormIncludeRestaurantVars()) {
        this.$toast.error(`Restaurant variables can only be used in the "Restaurant" segment or in "All Users" when the Leads filter is enabled and a restaurant is selected.`);
        return;
      }

      if (!["User", "UsersWithOrders", "NoRecentOrdersUsers", "InactiveOrderUsers", "MenutePayLowUsage", "HighPointUsers", "FirstOrderOrNewAccount22Hours", "Guest", "Admin", "Courier"].includes(this.form.segment) && this.isFormIncludeVar("restaurant_list")) {
        this.$toast.error(`Restaurant list variable cannot be used in the "${this.form.segment}" segment.`);
        return;
      }

      if (this.form.channels !== "email" && this.isFormIncludeVar("menute_cta_button")) {
        this.$toast.error(`Menute CTA Button can only be used with the Email channel.`);
        return;
      }

      if (!this.form.voucher && this.isFormIncludeCampaignVars()) {
        this.$toast.error(`The message contains campaign variables even though no voucher is selected!`);
        return;
      }

      if (this.isFormIncludeRetentionVoucherVars() && !this.showUniqueVoucherSettings) {
        this.$toast.error(`Unique voucher variables can only be used for user-based CRM voucher segments.`);
        return;
      }

      if (this.isFormIncludeRetentionVoucherVars() && !this.form.data?.retention_voucher_enabled) {
        this.$toast.error(`Retention voucher variables require the unique retention voucher to be enabled.`);
        return;
      }

      if (this.showLeadRestaurantField && this.form.data['restaurant_id'] && this.form.voucher?.resto_id === null) {
        const confirmReset = await this.mSwal.fire({
          text: "Do you still want to continue?",
          title: "There are no restaurant restrictions for the selected voucher!",
          icon: "warning",
          confirmButtonText: "Continue",
          cancelButtonText: "Cancel"
        });

        if (confirmReset.isDismissed) return;
      }

      const savesWithoutSending = saveOnly || updateExisting;
      const segmentLabel = this.getSegmentLabel(this.form);
      const audienceFilterBadges = this.selectedAudienceFilters.length
        ? this.selectedAudienceFilters.map(filter => `<span class="badge badge-pill badge-info mr-1 mb-1">${filter}</span>`).join('')
        : '';
      const appLabels = (this.form.apps?.pluck('label') || []);
      const appBadges = appLabels.length
        ? appLabels.map(l => `<span class="badge badge-pill badge-light mr-1 mb-1">${l}</span>`).join('')
        : '<span class="text-muted">No app</span>';


      const customLink = this.form.data?.custom_link?.url
        ? `<a href="${this.form.data.custom_link.url}" target="_blank" rel="noopener">${this.form.data.custom_link.title || this.form.data.custom_link.url}</a>`
        : '';

      const metaBadges = [];
      if (this.form.put_inbox) {
        const inboxSuffix = this.form.active_until_is_null
          ? ' - no end date'
          : (this.form.active_until ? ' - ' + this.form.active_until : '');
        metaBadges.push(`<span class="badge badge-pill badge-info mr-1">Inbox${inboxSuffix}</span>`);
      }
      if (this.form.data?.in_app) {
        metaBadges.push(`<span class="badge badge-pill badge-success mr-1">In-App${this.form.data?.disable_push_notification ? ' · push off' : ''}</span>`);
      }
      if (customLink) {
        metaBadges.push(`<span class="badge badge-pill badge-warning mr-1">Link: ${customLink}</span>`);
      }

      const imageBox = this.form.data?.image
        ? `<div class="mb-2">
             <img src="${this.form.data.image.image_url}" alt="Message image" class="img-fluid rounded border" />
           </div>`
        : '';

      const previewHtml = `
        <div class="preview-container border rounded p-3">
          <div class="d-flex justify-content-between mb-2">
            <div>👥 <span class="badge badge-light"> ${segmentLabel}</span></div>
            <div class="text-muted" style="font-weight: 500;">🕑 ${executionTime ? this.momentScheduledTime.format('MMMM Do YYYY, HH:mm') : (savesWithoutSending ? 'Not sent' : 'Now')}</div>
          </div>

          ${ audienceFilterBadges ? `<div class="mb-2">${audienceFilterBadges}</div>` : '' }

          <div class="mb-2">
            📢 <span class="badge badge-primary mr-1">${this.form.channels}</span>
            ${ this.channelHasPush ? appBadges : '' }
          </div>

          ${ metaBadges.length ? `<div class="mb-2">${metaBadges.join('')}</div>` : '' }

          ${imageBox}

          <div class="flex-fill">
            <div class="mb-2">
              <div class="d-flex align-items-center mb-1"><span class="badge badge-light mr-2">NL</span><span>${this.form.title.nl || '<em class="text-muted">Empty</em>'}</span></div>
              <div class="d-flex align-items-center mb-1"><span class="badge badge-light mr-2">FR</span><span>${this.form.title.fr || '<em class="text-muted">Empty</em>'}</span></div>
              <div class="d-flex align-items-center"><span class="badge badge-light mr-2">EN</span><span>${this.form.title.en || '<em class="text-muted">Empty</em>'}</span></div>
            </div>

            <div class="mb-2">
              <div class="d-flex align-items-start mb-2"><span class="badge badge-light mr-2">NL</span><div class="border rounded p-2 w-100">${this.form.message.nl || '<em class="text-muted">Empty</em>'}</div></div>
              <div class="d-flex align-items-start mb-2"><span class="badge badge-light mr-2">FR</span><div class="border rounded p-2 w-100">${this.form.message.fr || '<em class="text-muted">Empty</em>'}</div></div>
              <div class="d-flex align-items-start"><span class="badge badge-light mr-2">EN</span><div class="border rounded p-2 w-100">${this.form.message.en || '<em class="text-muted">Empty</em>'}</div></div>
            </div>
          </div>
        </div>
      `;

      const confirmOptions = {
        customClass: {
          container: 'message-swal2-container',
        },
        title: updateExisting ? 'Confirm Message Update' : (saveOnly ? 'Confirm Template Save' : `Confirm ${isTest ? 'Test' : ''} Message Sending`),
        html: previewHtml,
        icon: "question",
        confirmButtonText: updateExisting ? 'Save changes' : (saveOnly ? 'Save template' : 'Send'),
      };

      if (!savesWithoutSending) {
        Object.assign(confirmOptions, {
          input: "checkbox",
          inputValue: 0,
          inputPlaceholder: "I approve the bulk sending of this message.",
          inputValidator: (result) => {
            return !result && "Please check the confirmation box for this message to be sent!";
          }
        });
      }

      const confirm = await this.mSwal.fire(confirmOptions);

      if (confirm.isConfirmed) {
        const url = updateExisting
          ? this.$backend.CRM_MESSAGES.UPDATE.replace("{id}", this.editingMessageId)
          : this.$backend.CRM_MESSAGES.SEND;
        this.sending = true;

        try {
          const payload = this.buildPayload({ executionTime, isTest, saveOnly: savesWithoutSending });

          const { data } = updateExisting
            ? await this.$axios.put(url, payload)
            : await this.$axios.post(url, payload);

          const successMessage = savesWithoutSending && data.message_id
            ? `${data.message} (#${data.message_id})`
            : data.message;
          this.$toast.success(successMessage);
          if (isTest == false) {
            localStorage.removeItem('user-message')
            this.editingMessageId = null;
            this.$router.push({ name: "Message List" });
          }
        } catch (error) {
          this.sending = false;
          this.$toast.error(typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : this.errors
          );
        } finally {
          this.sending = false;
        }
      }

    },

    async countAudience() {
      this.audienceCountLoading = true;
      this.audienceCountResult = null;

      try {
        const payload = this.buildPayload({ isTest: false });
        const { data } = await this.$axios.post(this.$backend.CRM_MESSAGES.COUNT_AUDIENCE, payload);
        this.audienceCountResult = data;
      } catch (error) {
        this.$toast.error(typeof this.errors === 'object'
          ? Object.values(this.errors).join()
          : this.errors
        );
      } finally {
        this.audienceCountLoading = false;
      }
    },

    async scheduleSend() {
      this.showScheduleSendModal = false;
      this.send({ executionTime: this.scheduledTime });
    },

    async getPushApps() {
      if (!this.channelHasPush) {
        this.form.apps = [];
        this.form.data["custom_link"] = { "url": null, "title": null };
        this.form.data["in_app"] = false;
        this.form.data["disable_push_notification"] = false;
        return;
      }

      !this.pushApps.length && await this.$axios.get(this.$backend.PUSHER.APP_LIST)
        .then(({ data }) => {
          this.pushApps = data;
        });
    },

    async getAllLanguages() {
      this.languages = JSON.parse(localStorage.getItem('languages')) || [];

      !this.languages.length && await this.$axios.get(this.$backend.SETTINGS.GET_LANGUAGES)
        .then(({ data }) => (this.languages = data))
        .then(() => localStorage.setItem('languages', JSON.stringify(this.languages)))
    },

    async getCampaignMailContents() {
      await this.$axios.get(this.$backend.CAMPAIGNS.MAIL_CONTENT)
        .then(({ data }) => {
          this.form.message = data.message;
          this.form.title = data.title;
        });
    },

    async getVouchers(val) {
      if (val.length < 3) {
        return;
      }

      this.loading = true;

      var params = {
        code: val,
        applyAvailable:true,
        restaurant_id: this.showLeadRestaurantField ? this.form.data['restaurant_id'] : null
      };

      await this.$axios.get(this.$backend.VOUCHERS.LIST, { params: params })
        .then(({ data }) => {
          this.vouchers = data;
          this.loading = false;
        });
    },

    async getAllRestaurants() {
      this.loading = true;
      await this.$axios.get(this.$backend.RESTAURANT.GET_ALL_NAME)
        .then(({ data }) => {
          this.restaurants = data;
        });
      this.loading = false;
    },

    onImageSelected(image) {
      this.form.data.image = image;
    },

    removeImage() {
      this.form.data.image = null;
    },
  }
};
</script>

<style>
form.message-form .form-control,
form.message-form option,
form.message-form .vs__search,
form.message-form .vs__dropdown-menu,
form.message-form .vs__selected {
  font-size: 1.125rem;
  color: #9b0a0a;
  font-weight: 500;
}

form.message-form .vs__dropdown-toggle {
  padding: 2px 0 6px;
}

form.message-form .message > div:not(.spinner-border) {
  margin-top: 1rem;
  width: 100%;
}

form.message-form .tab-content {
  padding: 0.5rem 0.5rem 0 0;
}

form.message-form label.c-switch {
  margin-top: 0.5rem;
}

.message-swal2-container .swal2-html-container {
  display: flex !important;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
  font-size: 14px;
}
.message-swal2-container .swal2-html-container > ul {
  padding-left: 0rem;
}
.message-swal2-container .swal2-html-container > ul li {
  display: flex !important;
  flex-direction: column;
  justify-content: flex-start;
}

.message-editor {
  margin-bottom: 0;
}
.message-editor .tox.tox.tox-tinymce {
  min-height: 640px;
}

.schedule-send-modal .vc-container {
  box-shadow: none !important;
  border: none !important;
}

#swal2-html-container .preview-container {
  .badge {
    font-size: 90%;
  }
}
</style>

<style scoped>
.settings {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0.5rem;

  background-color: #D8E2DC; /* Sage Green */
  color: #0F172A;
  border-radius: 0.25rem;
  border: 1px solid transparent;
  box-shadow: 0 6px 18px rgba(17,24,39,0.06);
}
</style>
